import * as React from 'react';
import LureoButton from '@/common/LureoButton';
import { cn } from '@/utils/utils';
import { useThemeVersion } from '@/modules/theme';
import { LureoButtonTooltipPosition } from '@/types/tooltipTypes';

// The editor's chrome over the 3D view: bars of controls (the project bar, the view controls, the connection and story
// health bars), each the same height, surface, padding and shadow, holding the same icon buttons. A bar's button opens
// a Panel (components/ui/panel) as its trigger: it shows itself selected while its panel is open.

/** The bar's surface with a hole where each part marked data-chrome-window sits, in the part's own corners, following them as they move or resize and when the theme changes. */
function useWindows(bar: React.RefObject<HTMLDivElement>, on: boolean) {
  const [mask, setMask] = React.useState<React.CSSProperties>();
  // A theme change can change the parts' corners without changing their size
  const themeVersion = useThemeVersion();
  React.useLayoutEffect(() => {
    const element = bar.current;
    const windows = element ? [...element.querySelectorAll<HTMLElement>('[data-chrome-window]')] : [];
    if (!on || !element || !windows.length) return;
    const measure = () => {
      const [width, height] = [element.offsetWidth, element.offsetHeight];
      const holes = windows.map(part => {
        // The part's place in the bar, unscaled by any transform on the way in
        let [x, y] = [0, 0];
        for (let node: HTMLElement | null = part; node && node !== element; node = node.offsetParent as HTMLElement | null) [x, y] = [x + node.offsetLeft, y + node.offsetTop];
        const [w, h] = [part.offsetWidth, part.offsetHeight];
        const r = Math.min(w / 2, h / 2, parseFloat(getComputedStyle(part).borderTopLeftRadius) || 0);
        // Four corner arcs with straight sides between, so small corners stay inside the part
        return `M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + h - r}A${r} ${r} 0 0 1 ${x + w - r} ${y + h}H${x + r}A${r} ${r} 0 0 1 ${x} ${y + h - r}V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`;
      });
      const shape = `<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'><path fill-rule='evenodd' d='M0 0H${width}V${height}H0Z ${holes.join(' ')}'/></svg>`;
      const image = `url("data:image/svg+xml,${encodeURIComponent(shape)}")`;
      setMask({ maskImage: image, WebkitMaskImage: image, maskSize: '100% 100%', WebkitMaskSize: '100% 100%' });
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(element);
    windows.forEach(part => watch.observe(part));
    return () => watch.disconnect();
  }, [bar, on, themeVersion]);
  return mask;
}

/** A bar of controls floating over the 3D view, across or (vertical) standing up, as the libraries' rail and the phone's menus are. Windowed, the parts in it marked data-chrome-window are cut out of its surface, so they show the scene behind the bar: the story size meter's glass. Top, it is one of the editor's top bars, in their own fill. Its corners are its buttons' corners, so its buttons sit in it in the same shape, per corner where the theme sets each. */
export const ChromeBar = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & { vertical?: boolean; windowed?: boolean; top?: boolean }>(
  ({ vertical = false, windowed = false, top = false, className, children, ...props }, ref) => {
    const own = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(ref, () => own.current as HTMLDivElement);
    const mask = useWindows(own, windowed);
    return (
      <div
        ref={own}
        className={cn(
          'pointer-events-auto flex shrink-0 items-center gap-0.5 rounded-lureo-radius-button-pill surface-chrome p-1 shadow-lureo-shadow-overlay',
          vertical ? 'w-11 flex-col' : 'h-11',
          // One of the editor's top bars, in the top bar's own fill
          top && 'surface-editor-bar',
          // The surface moves to a layer under the controls, with the windows cut out of it
          windowed && 'relative isolate bg-none!',
          className,
        )}
        {...props}
      >
        {windowed && <span aria-hidden className={cn('surface-chrome pointer-events-none absolute inset-0 -z-10 rounded-lureo-radius-button-pill', top && 'surface-editor-bar')} style={mask} />}
        {children}
      </div>
    );
  },
);
ChromeBar.displayName = 'ChromeBar';

/** The rule between groups of controls in a bar, as production drew it: 2px by 32px. */
export function ChromeDivider({ className }: { className?: string }) {
  return <span aria-hidden className={cn('mx-2 h-8 w-0.5 shrink-0 bg-lureo-line-strong', className)} />;
}

interface ChromeButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** What it does, as its tooltip and accessible name. */
  label: string;
  /** Its icon, drawn h-6 w-6 (h-5 w-5 when compact). */
  children: React.ReactNode;
  /** Shown selected: a mode that is on (its panel being open shows the same). */
  active?: boolean;
  tooltipPosition?: LureoButtonTooltipPosition;
  /** A dot on the icon: something new behind it. */
  badge?: boolean;
  /** The timeline's and the properties tab bar's smaller icons: drawn h-5 w-5, as production drew them there. */
  compact?: boolean;
  /** The timeline's transport: no wash or ring under the pointer, only its icon reacts. */
  bare?: boolean;
  /** Disables it and says why as its tooltip in place of its label (offline, say). */
  disabledReason?: string | null;
}

/** A bar's icon button: the editor rail's look, its tooltip below it (its disabledReason, when it has one). */
export const ChromeButton = React.forwardRef<HTMLButtonElement, ChromeButtonProps>(
  ({ label, children, active = false, tooltipPosition = 'bottom', badge = false, compact = false, bare = false, className, ...props }, ref) => {
    const open = (props as { 'data-state'?: string })['data-state'] === 'open';
    return (
      <LureoButton
        ref={ref}
        variant="menu"
        isSelected={active}
        aria-label={label}
        tooltipContent={open ? undefined : label}
        tooltipPosition={tooltipPosition}
        tooltipOffset={10}
        className={cn('relative h-9 w-9 shrink-0 rounded-lureo-radius-button-pill p-0', compact ? '[&_svg]:h-5 [&_svg]:w-5' : '[&_svg]:h-6 [&_svg]:w-6', bare && 'hover:border-transparent hover:before:opacity-0 active:before:opacity-0', className)}
        {...(props as object)}
      >
        {children}
        {badge && <span aria-hidden className="!absolute right-1.5 top-1.5 !z-20 h-2 w-2 rounded-full bg-lureo-badge" />}
      </LureoButton>
    );
  },
);
ChromeButton.displayName = 'ChromeButton';
